import { Image } from "@/components/image";
import { ProfileIcon, type IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { Icon, type IconSize } from "./Icon";
import { useSurface, type SurfaceKind } from "./Surface";
import { SURFACE_BACKGROUND } from "./surface-background";

/** Classes of `Avatar`: a circle that crops an image or centers a fallback glyph. */
export const avatarVariants = cva("inline-grid shrink-0 place-items-center overflow-hidden rounded-full", {
  variants: {
    size: {
      sm: "h-8 w-8",
      md: "h-10 w-10",
      lg: "h-16 w-16",
      xl: "h-20 w-20",
      "2xl": "h-28 w-28",
    },
    ring: { true: "ring-2 ring-card", false: "" },
  },
  defaultVariants: { size: "md", ring: false },
});

type AvatarSize = NonNullable<VariantProps<typeof avatarVariants>["size"]>;

const AVATAR_PX: Record<AvatarSize, number> = { sm: 32, md: 40, lg: 64, xl: 80, "2xl": 112 };
const AVATAR_GLYPH: Record<AvatarSize, IconSize> = { sm: "sm", md: "xl", lg: "2xl", xl: "3xl", "2xl": "4xl" };

/**
 * Props of `Avatar`; an image needs `alt` (and may pass `crc`/`priority` to `Image`), otherwise `icon` (default: user) is drawn.
 * `surface` pins an opaque disc of that surface (image and fallback alike), for logos that overlap a banner.
 */
export type AvatarProps = VariantProps<typeof avatarVariants> &
  Omit<HTMLAttributes<HTMLSpanElement>, "children"> & { surface?: SurfaceKind } &
  (
    | { src: string; alt: string; crc?: string; priority?: boolean; icon?: never }
    | { src?: undefined; alt?: string; crc?: never; priority?: never; icon?: IconGlyph }
  );

/** Round image (logo, profile photo) on the surrounding surface colour, with an icon fallback. */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { src, alt, crc, priority, icon, size, ring, surface: pinnedSurface, className, ...rest },
  ref,
) {
  const resolvedSize = size ?? "md";
  const contextSurface = useSurface();
  const surface = pinnedSurface ?? contextSurface;
  const fill = src ? SURFACE_BACKGROUND[surface] : pinnedSurface ? `${SURFACE_BACKGROUND[pinnedSurface]} text-primary` : "bg-primary/10 text-primary";
  return (
    <span
      {...rest}
      ref={ref}
      role={!src && alt ? "img" : undefined}
      aria-label={!src ? alt : undefined}
      className={cn(avatarVariants({ size, ring }), fill, className)}
    >
      {src ? (
        <Image
          src={src}
          alt={alt}
          crc={crc}
          priority={priority}
          width={AVATAR_PX[resolvedSize]}
          className="h-full w-full"
        />
      ) : (
        <Icon as={icon ?? ProfileIcon} size={AVATAR_GLYPH[resolvedSize]} />
      )}
    </span>
  );
});
